import { router } from '@/router';
import { createApp } from 'vue';
import App from './App.vue';

import '@carrot/design-tokens';
import '@carrot/design-components';
import '@carrot/design-theme-default/gold';

import { applySkin, type SkinCatalogue, registerSkin } from '@carrot/design-runtime';
import { useTheme } from '@carrot/design-runtime-vue';
import skinCatalogue from './skins/carrot/generated/carrot-skin.catalogue.json';
import './skins/carrot/generated/carrot-skin.css';
import './skins/carrot/carrot-skin-component-tweaks.css';
import './styles/app.css';

import { carrotTracking, debugProvider } from '@carrot/tracking-vue';
// Meta Pixel provider ships in @carrot/tracking-vue-meta - wire it in once
// there's a real pixel ID:
//   import { metaPixelProvider } from '@carrot/tracking-vue-meta';
//   metaPixelProvider({ pixelId: '<real-id>' })

registerSkin(skinCatalogue as SkinCatalogue);


/*
  🔴 Registering is not wearing. Every skin rule keys off `[data-skin~="..."]` rather than `:root`,
  so nothing is styled until the page says which skin it wears - and the chain is stamped, lowest
  first, because a skin carries only what it differs on.
*/
applySkin(skinCatalogue.name);

// `theme` here is just the app's own identifier for the `data-theme`
// attribute (decoupled from the imported design-theme-default variant) -
// `mode` (light/dark/system) is what design-theme-default actually reacts to.
const theme = useTheme({
  defaultTheme: 'carrotcorp',
  // This site is light-only - the skin hardcodes white surfaces and there is
  // no dark design. Without this the controller defaults to mode 'system' and
  // follows the visitor's prefers-color-scheme, which silently flips every
  // dark-first DS token. See styles/app.css for what that broke.
  defaultMode: 'light',
  storageKey: 'carrotcorp.theme',
});
if (theme.theme.value !== 'carrotcorp') {
  theme.setTheme('carrotcorp');
}
// `defaultMode` only applies to a FIRST visit: the controller resolves
// `persisted.mode ?? options.defaultMode`, so anyone carrying a
// {"mode":"system"} entry in localStorage from before this change would keep
// following their OS regardless. Force it, exactly as the theme is forced above.
if (theme.mode.value !== 'light') {
  theme.setMode('light');
}

const app = createApp(App);

app.use(router);
app.use(carrotTracking, {
  // debugProvider logs to the console instead of sending anywhere - swap
  // in metaPixelProvider (or others) once there's something real to send to.
  providers: [debugProvider()],
  router,
  // Gated on consent (not `consentless: true`) since this is a public site -
  // call useTracking().setConsent('granted') from a cookie-consent banner.
  consent: 'pending',
});

app.mount('#app');
